import React, { useState, useEffect } from 'react';
import { Container, Box, TextField, Button, Paper, Autocomplete, Checkbox, FormControlLabel } from '@mui/material';
import * as endPoints from '../assets/js/endpoints';
import { Helmet } from 'react-helmet';
import FadeItem from '../components/fadeItem';
import usePageBackground from '../hooks/usePageBackground';
import { useFadeInOnScroll } from '../theme/global';
import * as globalstyles from '../theme/global';
import Loader from '../components/loader';
import { useNotification } from '../assets/contexts/notificationContext';

const PideTuCita = () => {
  const initialForm = {
    name: '',
    lastName: '',
    email: '',
    phone: '',
    serviceId: '',
    message: '',
    providerId: '',
    date: '',
    time: ''
  };
  const [form, setForm] = useState(initialForm);
  const [providers, setProviders] = useState([]);
  const [services, setServices] = useState([]);
  const [filteredProviders, setFilteredProviders] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [noAvailability, setNoAvailability] = useState(false);
  const [noSlotsForDate, setNoSlotsForDate] = useState(false);
  const [hideProvidersWithoutActiveServices, setHideProvidersWithoutActiveServices] = useState(true);
  const { showNotification } = useNotification();

  usePageBackground('form-background-color');
  const containerRef = useFadeInOnScroll();

  // Load saved form data on mount so the user doesn't lose information
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem('pideTuCitaForm');
      if (stored) {
        setForm(prev => ({ ...prev, ...JSON.parse(stored) }));
      }
    } catch (_err) {
    }
  }, []);

  // Persist form data while the user types
  useEffect(() => {
    sessionStorage.setItem('pideTuCitaForm', JSON.stringify(form));
  }, [form]);

  useEffect(() => {
    const loadProviders = async () => {
      try {
        const data = await endPoints.getProviders();
        const list = Array.isArray(data) ? data : data?.data || [];
        setProviders(list);
      } catch (_err) {
      }
    };
    loadProviders();
  }, []);

  useEffect(() => {
    const loadServices = async () => {
      try {
        const data = await endPoints.getAllServices();
        const list = Array.isArray(data) ? data : data?.data || [];
        setServices(list.filter(service => service?.active !== false));
      } catch (_err) {
      }
    };
    loadServices();
  }, []);

  const checkProviderAvailability = async (providerId) => {
    const today = new Date();
    for (let i = 0; i < 30; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      const dateStr = d.toISOString().split('T')[0];
      try {
        const slots = await endPoints.getAvailabilitySlots(providerId, dateStr);
        if (Array.isArray(slots) && slots.length) {
          return true;
        }
      } catch (_err) {
        break;
      }
    }
    return false;
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;
    if (name === 'serviceId') {
      setForm(prev => ({ ...prev, serviceId: value, providerId: '' }));
      setNoAvailability(false);
    } else if (name === 'providerId') {
      setForm(prev => ({ ...prev, providerId: value, date: '', time: '' }));
      setTimeSlots([]);
      setNoSlotsForDate(false);
      if (value) {
        const hasAvailability = await checkProviderAvailability(value);
        setNoAvailability(!hasAvailability);
      } else {
        setNoAvailability(false);
      }
    } else {
      setForm(prev => ({ ...prev, [name]: value }));
    }
  };

  useEffect(() => {
    const fetchSlots = async () => {
      if (form.providerId && form.date && !noAvailability) {
        try {
          const slots = await endPoints.getAvailabilitySlots(form.providerId, form.date);
          setTimeSlots(slots);
          setNoSlotsForDate(slots.length === 0);
        } catch (_err) {
          setTimeSlots([]);
          setNoSlotsForDate(true);
        }
      } else {
        setTimeSlots([]);
        if (!form.date) {
          setNoSlotsForDate(false);
        }
      }
      setForm(prev => ({ ...prev, time: '' }));
    };
    fetchSlots();
  }, [form.providerId, form.date, noAvailability]);


  useEffect(() => {
    const filtered = providers.filter(provider => {
      if (provider?.active === false) {
        return false;
      }

      const providerServices = Array.isArray(provider.services) ? provider.services : [];
      const activeProviderServices = providerServices.filter(service => service?.active !== false);

      if (activeProviderServices.length === 0) {
        return false;
      }

      if (!form.serviceId) {
        return true;
      }

      return activeProviderServices.some(service => String(service.id) === String(form.serviceId));
    });

    setFilteredProviders(filtered);
  }, [providers, form.serviceId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const provider = providers.find(p => String(p.id) === String(form.providerId));
    const service = services.find(s => String(s.id) === String(form.serviceId));
    const notes = form.email
      ? `${form.message}\nEmail: ${form.email}\nTeléfono: ${form.phone}`
      : `${form.message}\nTeléfono: ${form.phone}`;
    const payload = {
      employee_id: form.providerId,
      employee_name: provider
        ? `${provider.name} ${provider.lastName || provider.last_name || ''}`.trim()
        : undefined,
      client_name: `${form.name} ${form.lastName}`.trim(),
      service_id: form.serviceId || null,
      service_name: service ? service.name : undefined,
      date: form.date,
      time: form.time,
      notes,
      client_phone: form.phone,
      status: 'Pendiente',
      type: 'appointment'
    };
    if (form.email) payload.client_email = form.email;
    try {
      await endPoints.bookAppointment(payload);
      showNotification('Cita concertada', 'success');
      setForm(initialForm);
      sessionStorage.removeItem('pideTuCitaForm');
      setTimeout(() => window.location.reload(), 1000);
    } catch (err) {
      const message = err.response?.data?.message || err.response?.data?.error || 'Error al concertar la cita';
      showNotification(message, 'error');
       setTimeout(() => window.location.reload(), 1000);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <>
      <Helmet>
        <title>Pide tu cita | CES</title>
        <meta
          name="description"
          content="Solicita tu cita con nuestros profesionales."
        />
      </Helmet>
      <Container
        ref={containerRef}
        className="fade-up"
        sx={{ p: { xs: 1, sm: 2, md: 4 }, my: 2 }}
      >
        <Paper
          sx={{
            paddingY: 3,
            paddingX: { xs: '1em', sm: '2em', md: '4em' },
            marginY: { xs: '2em', md: '4em' },
            marginX: { xs: '1em', md: '6em' },
          }}
        >
          <FadeItem
            text="Pide tu cita"
            variant="h2"
            style={{
              color: '#80B779',
              fontFamily: 'Great Vibes, cursive',
              textAlign: 'center',
              paddingBottom: '0.5em',
              paddingTop: '0.5em',
            }}
          />
          <Box
            component="form"
            onSubmit={handleSubmit}
            className="pide-tu-cita-form"
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { sm: 2 },
                alignItems: 'center',
                '& > *:not(:last-child)': { mb: { xs: 2, sm: 0 }, alignSelf: 'stretch' },
              }}
            >
              <TextField
                type="text"
                name="name"
                label="Nombre"
                value={form.name}
                onChange={handleChange}
                fullWidth
                margin="normal"
                required
              />
              <TextField
                type="text"
                name="lastName"
                label="Apellidos"
                value={form.lastName}
                onChange={handleChange}
                fullWidth
                margin="normal"                
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { sm: 2 },
                alignItems: 'center',
                '& > *:not(:last-child)': { mb: { xs: 2, sm: 0 }, alignSelf: 'stretch'},
              }}
            >
              <TextField
                type="email"
                name="email"
                label="Correo electrónico"
                value={form.email}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
              <TextField
                type="tel"
                name="phone"
                label="Teléfono"
                value={form.phone}
                onChange={handleChange}
                fullWidth
                margin="normal"
                required
              />
            </Box>
             <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { sm: 2 },
                alignItems: 'center',
                '& > *:not(:last-child)': { mb: { xs: 2, sm: 0 }, alignSelf: 'stretch' },
              }}
            >

            <Autocomplete
            fullWidth
              options={services}
              getOptionLabel={option => option?.name || ''}
              value={services.find(s => String(s.id) === String(form.serviceId)) || null}
              onChange={(_event, newValue) => {
                const value = newValue ? newValue.id : '';
                handleChange({ target: { name: 'serviceId', value } });
              }}
              isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
         
              renderInput={params => (
                <TextField
                  {...params}
                  label="Servicio"
                  fullWidth
                  margin="normal"
                />
              )}
            />
            <Autocomplete
            fullWidth
              options={filteredProviders}
              getOptionLabel={option => option?.name || ''}
              value={filteredProviders.find(p => String(p.id) === String(form.providerId)) || null}
              onChange={(_event, newValue) => {
                const value = newValue ? newValue.id : '';
                handleChange({ target: { name: 'providerId', value } });
              }}
              isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
              disabled={!form.serviceId || noAvailability}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Profesional"
                  fullWidth
                  margin="normal"
                />
              )}
            />
            </Box>
            {noAvailability ? (
              <Box sx={{ mt: 2 }}>
                No hay citas disponibles para este profesional
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { sm: 2 },
                  alignItems: 'center',
                  '& > *:not(:last-child)': { mb: { xs: 2, sm: 0 }, alignSelf: 'stretch'},
                }}
              >
                <TextField
                  type="date"
                  name="date"
                  label="Fecha deseada"
                  value={form.date}
                  onChange={handleChange}
                  fullWidth
                  margin="normal"
                  InputLabelProps={{ shrink: true }}
                />
                {form.date && noSlotsForDate ? (
                  <Box sx={{ mt: 2, width: '100%' }}>
                    No hay horas disponibles para el día seleccionado para este profesional
                  </Box>
                ) : (
                  <Autocomplete
                  fullWidth
                    options={timeSlots}
                    value={form.time || null}
                    onChange={(_event, newValue) => {
                      handleChange({ target: { name: 'time', value: newValue || '' } });
                    }}
                    disabled={!timeSlots.length}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label="Hora deseada"
                        fullWidth
                        margin="normal"
                      />
                    )}
                  />
                )}
              </Box>
            )}
            <TextField
              name="message"
              label="Motivo de la cita"
              value={form.message}
              onChange={handleChange}
              multiline
              rows={4}
              fullWidth
              margin="normal"
            />
            <Button variant="contained" type="submit" sx={{ ...globalstyles.buttonStandard, cursor: 'pointer', padding: '0.6em 2rem', display: 'block', margin: '1em auto 0 auto' }}>
              Enviar
            </Button>
          </Box>
        </Paper>
      </Container>
    </>
  );
};

export default PideTuCita;
